import { CommonModule } from '@angular/common';
import { Component, computed, effect, ElementRef, input, model, viewChild, ChangeDetectionStrategy, signal } from '@angular/core';
import { __ } from '../../functions/object.functions';
import { BaseComponent } from '../base/base.component';
import { ImageResolution, ImageFit, IMAGE_RESOLUTION } from '../../types/image.types';

@Component({
  selector: 'immo-image',
  imports: [
    CommonModule
  ],
  templateUrl: './image.component.html',
  styleUrl: './image.component.css',
})
export class ImageComponent extends BaseComponent {

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly IMAGE_RESOLUTION = IMAGE_RESOLUTION;

  private readonly retries = signal<number>(0);

  private readonly MAX_RETRIES = 3;

  // -----------------------------------------------------------------------------------------------------
  // @ INPUT VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /**
   * The source URL of the image.
   */
  readonly src = input.required<string>();

  /**
   * The alt text for the image.
   */
  readonly alt = input.required<string>();

  /**
   * The selected image render size.
   *  - If `responsive`, the image will adapt to screen size.
   *  - Set to `none` in order to use the exact image URL without any resolution suffix.
   * @default 'responsive'
   */
  readonly resolution = model<ImageResolution>('responsive');

  /**
   * Used to set the object-fit of the image.
   * @default 'contain'
   */
  readonly fit = input<string, ImageFit>('object-contain', {
    transform: (value: ImageFit) => {
      switch (value) {
        case 'cover':
          return 'object-cover';
        case 'contain':
          return 'object-contain';
        case 'fill':
          return 'object-fill';
        case 'scale-down':
          return 'object-scale-down';
        case 'none':
          return 'object-none';
        default:
          return 'object-contain';
      }
    }
  });

  /**
   * Used to directly set classes on the image element.
   */
  readonly imageClasses = input<string[], string[]>(['aspect-square'], {
    transform: (value: string[]) => {
      return Array.isArray(value) ? value : ['aspect-square'];
    }
  });

  /**
   * The default height of the image.
   */
  readonly defaultHeight = input<number>();

  /**
   * The default width of the image.
   */
  readonly defaultWidth = input<number>();

  readonly fallbackSrc = input<string>('images/150x150.png');

  // -----------------------------------------------------------------------------------------------------
  // @ COMPUTED VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly imageSource = computed<string>(() => {
    const resolution: ImageResolution = this.resolution();
    const src: string = this.src();
    const appendSlash: (q: string) => string = (q: string): string => q.endsWith('/') ? q : q + '/';

    if (this.resolution() === 'responsive') {
      return `${appendSlash(src)}${IMAGE_RESOLUTION.original}`;
    }

    if (__.IsNullOrUndefined(resolution) || this.resolution() === 'none') {
      return src;
    }

    return `${appendSlash(src)}${IMAGE_RESOLUTION[resolution]}`;
  })

  // -----------------------------------------------------------------------------------------------------
  // @ VIEW CHILDREN
  // -----------------------------------------------------------------------------------------------------

  private readonly imageRef = viewChild('image', { read: ElementRef<HTMLImageElement> });

  // -----------------------------------------------------------------------------------------------------
  // @ EFFECTS
  // -----------------------------------------------------------------------------------------------------

  private readonly imageLoadEffect = effect(() => {
    const image: HTMLImageElement = this.imageRef()?.nativeElement;

    if (!this.IsNullOrUndefined(image) && this.src()) {
      image.onerror = () => {
        if (['none', 'original'].includes(this.resolution())) {
          return image.src = this.fallbackSrc();
        }

        this.resolution.set('original');
        return image.src = this.imageSource();
      };
    }
  });
}

